import { makeStyles, tokens, Text, CheckboxOnChangeData } from "@fluentui/react-components";
import { ChevronDown16Regular, ChevronUp16Regular } from "@fluentui/react-icons";
import { StorageWrapper } from "@src/services/StorageWrapper";
import React, { useEffect, useMemo, useRef } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { DateSelectionOutput } from "./sections/DateSelectionSection";
import AllowPartialDownloadSection from "./sections/AllowPartialDownloadSection";
import RequireLoginSection from "./sections/RequireLoginSection";
import { TransferExpiresSection } from "./sections/TransferExpiresSection";
import { SendRemindersSection } from "./sections/SendRemindersSection";

const useStyles = makeStyles({
    toggle: {
        display: "inline-flex",
        alignItems: "center",
        cursor: "pointer",
        gap: "4px",
        paddingTop: "8px",
        paddingBottom: "4px",
    },
    toggleDisabled: {
        cursor: "default",
        opacity: 0.5,
    },
    toggleText: {
        color: tokens.colorBrandForeground1,
        fontSize: "12px",
    },
    toggleIcon: {
        color: tokens.colorBrandForeground1,
        fontSize: "12px",
    },
    container: {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        paddingTop: "4px",
        backgroundColor: tokens.colorNeutralBackground1Hover,
        borderRadius: tokens.borderRadiusMedium,
        padding: "8px",
    },
    divider: {
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        marginTop: "4px",
        marginBottom: "4px",
    },
});

export interface MoreOptionsProjectConfig {
    defaultExpireDays: number;
    transferExpiresByDefault: boolean;
    allowPartialDownload: boolean;
    requireNixLoginForDownload: boolean;
}

export interface MoreOptionsSettings {
    expireDateSelection: DateSelectionOutput;
    reminderDateSelection: DateSelectionOutput;
    allowPartialDownload: boolean;
    requireNixLoginForDownload: boolean;
}

export interface MoreOptionsFluent9ComponentProps extends LocalizeContextProps {
    disabled: boolean;
    noProjectSelected: boolean;
    projectConfig: MoreOptionsProjectConfig;
    moreOptionsSettings: MoreOptionsSettings;
    storageWrapper: StorageWrapper;
    isLoading: boolean;
    onSettingsChange?: (partial: Partial<MoreOptionsSettings>) => void;
}

export const MoreOptionsFluent9Component = withLocalize((props: MoreOptionsFluent9ComponentProps) => {
    const styles = useStyles();

    const containerRef = useRef<HTMLDivElement>(null);

    const [isExpanded, setIsExpanded] = React.useState(false);

    const canToggle = useMemo(() => !props.disabled && !props.isLoading, [props.disabled, props.isLoading]);

    useEffect(() => {
        if (isExpanded && containerRef.current) {
            containerRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
    }, [isExpanded]);

    useEffect(() => {
        if (props.isLoading || props.disabled) setIsExpanded(false);
    }, [props.isLoading, props.disabled]);

    const handleToggle = () => {
        if (canToggle) setIsExpanded((prev) => !prev);
    };

    const handleTransferExpiresChange = (output: DateSelectionOutput) => {
        props.onSettingsChange?.({ expireDateSelection: output });
    };

    const handleReminderChange = (output: DateSelectionOutput) => {
        props.onSettingsChange?.({ reminderDateSelection: output });
    };

    const handleAllowPartialDownloadChange = (_ev: any, data: CheckboxOnChangeData) => {
        props.onSettingsChange?.({ allowPartialDownload: !!data.checked });
    };

    const handleRequireLoginChange = (_ev: any, data: CheckboxOnChangeData) => {
        props.onSettingsChange?.({ requireNixLoginForDownload: !!data.checked });
    };

    return (
        <div data-testid="more-options">
            <div
                data-testid="more-options-toggle"
                className={`${styles.toggle}${!canToggle ? ` ${styles.toggleDisabled}` : ""}`}
                onClick={handleToggle}
                role="button"
                tabIndex={canToggle ? 0 : -1}
                aria-disabled={!canToggle}
                onKeyDown={(e) => {
                    if (canToggle && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        handleToggle();
                    }
                }}
                aria-expanded={isExpanded}
                aria-label="More options"
            >
                <Text className={styles.toggleText}>
                    {props.translate("SHARED.MOREOPTIONS.MOREOPTIONS_LABEL") as string}
                </Text>
                {isExpanded ? (
                    <ChevronUp16Regular className={styles.toggleIcon} />
                ) : (
                    <ChevronDown16Regular className={styles.toggleIcon} />
                )}
            </div>

            <div
                ref={containerRef}
                className={styles.container}
                data-testid="more-options-content"
                style={isExpanded ? undefined : { display: "none" }}
            >
                <TransferExpiresSection
                    dateSelection={props.moreOptionsSettings.expireDateSelection}
                    projectConfig={props.projectConfig}
                    storageWrapper={props.storageWrapper}
                    isInactive={props.isLoading || props.noProjectSelected}
                    onDateSelectionChange={handleTransferExpiresChange}
                />

                <div className={styles.divider} />

                <SendRemindersSection
                    dateSelection={props.moreOptionsSettings.reminderDateSelection}
                    isExpiredChecked={props.moreOptionsSettings.expireDateSelection.Checked}
                    expirationDate={props.moreOptionsSettings.expireDateSelection.SelectedDate}
                    storageWrapper={props.storageWrapper}
                    isInactive={props.isLoading || props.noProjectSelected}
                    onDateSelectionChange={handleReminderChange}
                />

                <div className={styles.divider} />

                <AllowPartialDownloadSection
                    checked={props.moreOptionsSettings.allowPartialDownload}
                    disabled={false}
                    isInactive={props.isLoading || props.noProjectSelected}
                    onChange={handleAllowPartialDownloadChange}
                />

                <div className={styles.divider} />

                <RequireLoginSection
                    checked={props.moreOptionsSettings.requireNixLoginForDownload}
                    disabled={props.projectConfig.requireNixLoginForDownload}
                    isInactive={props.isLoading || props.noProjectSelected}
                    onChange={handleRequireLoginChange}
                />
            </div>
        </div>
    );
});
